import { useState } from "react";
import { Expand, MapPin, Coins, Layers } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/shared/Reveal";
import { Lightbox } from "@/components/shared/Lightbox";

const OVERVIEW = [
  { image: "/depa/oversikt-nord.png", title: "Översikt Nord · Depå A & B", category: "Karta" },
  { image: "/depa/oversikt-syd.png", title: "Översikt Syd · Depå C–F & Team", category: "Karta" },
];

const AREAS = [
  { key: "a", name: "Depå A", place: "Pendlarparkering", zone: "Nord", info: "56 platser längs Centralvägen. Visas i två delar – infart 1–25 och 26–56.",
    images: [{ src: "/depa/area-a-1.png", label: "Del 1" }, { src: "/depa/area-a-2.png", label: "Del 2" }] },
  { key: "b", name: "Depå B", place: "Kolgrillen", zone: "Nord", info: "18 platser · 7,5 × 6 m (45 m²) och 10 × 6 m (60 m²).",
    images: [{ src: "/depa/area-b.png" }] },
  { key: "c", name: "Depå C", place: "Parkvägen", zone: "Syd", info: "52 platser · 7,5 × 6 m.",
    images: [{ src: "/depa/area-c.png" }] },
  { key: "d", name: "Depå D", place: "CIK-parkering", zone: "Syd", info: "25 platser · 7,5 × 6 m (45 m²) och 10 × 6 m (60 m²).",
    images: [{ src: "/depa/area-d.png" }] },
  { key: "e", name: "Depå E", place: "Södervägen", zone: "Syd", info: "10 platser · 7,5 × 6 m (45 m²) och 8,5 × 6 m (51 m²).",
    images: [{ src: "/depa/area-e.png" }] },
  { key: "f", name: "Depå F", place: "Badhuset", zone: "Syd", info: "11 platser · 7,5 × 6, 9 × 6 och 10 × 6 m.",
    images: [{ src: "/depa/area-f.png" }] },
  { key: "team", name: "Depå Team", place: "Serviceyta", zone: "Syd", paid: true, info: "Yta bokas och betalas på plats – 100 kr per löpmeter serviceyta.",
    images: [{ src: "/depa/area-team.png" }] },
];

// Flat lightbox list: overview maps first, then every area image (with running index)
let _i = OVERVIEW.length;
const AREA_TILES = AREAS.map((a) => ({
  ...a,
  images: a.images.map((im) => ({ ...im, idx: _i++, title: `${a.name} · ${a.place}${im.label ? " · " + im.label : ""}` })),
}));
const LB_ITEMS = [
  ...OVERVIEW,
  ...AREA_TILES.flatMap((a) => a.images.map((im) => ({ image: im.src, title: im.title, category: a.zone }))),
];

export const DepotAreas = () => {
  const [idx, setIdx] = useState(null);

  return (
    <section data-testid="depot-areas" className="py-20 sm:py-28 border-t border-white/8">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <SectionHeader kicker="Depåområden" title="Var vill du stå?" subtitle="Sju depåområden i Knivsta. Klicka på en karta för att se platserna i full storlek." />
        </Reveal>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {OVERVIEW.map((o, i) => (
            <Reveal key={o.image} delay={i * 0.08}>
              <button type="button" data-testid={`depot-overview-${i}`} onClick={() => setIdx(i)}
                className="group relative block w-full overflow-hidden rounded-2xl border border-white/10">
                <img src={o.image} alt={o.title} loading="lazy" className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4 flex items-center gap-2 text-left">
                  <MapPin className="w-4 h-4 text-amber-500" />
                  <span className="font-display text-lg font-bold uppercase text-white">{o.title}</span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {AREA_TILES.map((a, i) => (
            <Reveal key={a.key} delay={i * 0.05}>
              <div data-testid={`depot-area-${a.key}`} className="group flex flex-col rounded-2xl border border-white/10 bg-[#14161A] overflow-hidden hover:border-amber-500/50 transition-colors">
                <div className={`bg-[#f4f4f5] grid ${a.images.length > 1 ? "grid-cols-2" : "grid-cols-1"} gap-px`}>
                  {a.images.map((im) => (
                    <button key={im.src} type="button" data-testid={`depot-img-${a.key}${im.label ? "-" + im.label.split(" ")[1] : ""}`} onClick={() => setIdx(im.idx)} className="relative block">
                      <img src={im.src} alt={im.title} loading="lazy" className="w-full h-44 object-contain" />
                      {im.label && (
                        <span className="absolute top-2 left-2 rounded-full bg-[#0B0C0E] px-2.5 py-1 text-[10px] font-mono uppercase tracking-wide text-amber-400 flex items-center gap-1">
                          <Layers className="w-3 h-3" /> {im.label}
                        </span>
                      )}
                      <span className="absolute top-2 right-2 grid place-items-center h-8 w-8 rounded-full bg-black/60 text-white/90 opacity-0 group-hover:opacity-100 transition-opacity"><Expand className="w-4 h-4" /></span>
                    </button>
                  ))}
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-display text-xl font-black uppercase text-white leading-none">{a.name}</h3>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">{a.place}</span>
                  </div>
                  <p className="mt-2 text-sm text-white/60">{a.info}</p>
                  {a.images.length > 1 && (
                    <p className="mt-2 text-xs font-mono text-amber-400/90">Obs! Depå A visas i 2 delar – se Del 1 & Del 2 ovan.</p>
                  )}
                  {a.paid && (
                    <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-mono uppercase tracking-wide text-amber-400">
                      <Coins className="w-3.5 h-3.5" /> Betalas på plats
                    </div>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {idx !== null && <Lightbox items={LB_ITEMS} index={idx} onClose={() => setIdx(null)} onNav={setIdx} />}
    </section>
  );
};
